import Controller, { OnBeforeSubmit, OnClick, OnMount, OnUnmount } from '@philips-internal/oswald-ui-core/lib/client';
import BaseController from '../BaseController';
import { FilterAPI, PanelAPI } from '@philips-internal/oswald-ui-components';
import { DateFilterUtils } from '../../utils/date-filter-utils';
import { ClickEvent, MountEvent, SubmitEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { applicationContext } from '@philips-internal/oswald-ui-foundation';
import { ApplicationRoute } from '../../constants/routes';
import { FragmentNames, IntakesAndOutputsAliases, IntakesAndOutputsListAliases } from '../../codegen';
import { IntakesAndPutsContext } from './intakes-and-outputs-service';
import { ISO_DATE_FORMAT } from '@philips-internal/oswald-ui-model/lib/formatters/date/date-utils';
import moment from 'moment';
import { Value } from '@philips-internal/oswald-ui-core';

import { ToastLevel } from '@philips-internal/oswald-ui-constants';
import ShowToast from '../../utils/toast-service';
import { HttpService, MessageService } from '@philips-internal/oswald-ui-core/lib/services';
import { Inject } from '@odinjs/odin';
import InformationBarUtils from '../commons/header/information-bar-header-utils';
import { Endpoint } from '../../constants/endpoints';
import { ItemPepType, RulesItemConfiguration } from '../../constants/utils-pep';
import { YesNoType } from '../../utils/enum-utils';

enum CreateType {
  INTAKE = 'G',
  OUTPUT = 'P',
}

@Controller({ component: IntakesAndOutputsAliases.INTAKES_AND_OUTPUTS_LAYOUT, fragment: FragmentNames.INTAKES_AND_OUTPUTS_LIST })
class IntakesAndOutputsController extends BaseController {

  @Inject(IntakesAndOutputsListAliases.PANEL_GLOBAL_ACTIONS)
  private panelGlobalActionsApi!: PanelAPI;

  internalNavigation = true;

  @OnMount( IntakesAndOutputsListAliases.LAYOUT_INTAKES_OUTPUTS_TAB )
  handleMountLayout(): void {
    this.checkItemEditPermission()
      .finally(() => {
        const allowInfo = InformationBarUtils.allowInfo && IntakesAndPutsContext.getAllowEdit();
        this.panelGlobalActionsApi.setVisible(allowInfo);
      });
  }

  @OnMount({ target: IntakesAndOutputsListAliases.FILTER_INTAKES_OUTPUTS })
  handleFilterMount(event: MountEvent): void {
    const { currentDate } = DateFilterUtils.getDateRange();

    const record = {
      finalDate: currentDate,
      initialDate: moment().subtract(1, 'months').format(ISO_DATE_FORMAT),
    };

    IntakesAndPutsContext.setInternalNavegation(false);

    const filter = event.target as FilterAPI;
    const state = IntakesAndPutsContext.get();

    if (state) {
      Object.assign(record, IntakesAndPutsContext.get());
    }

    filter.setData({ ...record });
    filter.submit();
  }

  @OnUnmount(IntakesAndOutputsListAliases.FILTER_INTAKES_OUTPUTS)
  handleUnmountFilter(event: MountEvent): void {
    const filter = event.target as FilterAPI;
    IntakesAndPutsContext.set(filter.getRecord() as Record<string, Value>);
  }

  @OnClick({target: IntakesAndOutputsListAliases.INTAKES_OUTPUTS_OUTPUTS_CARD })
  handleCardClick(event: ClickEvent): void {
    const { releaseDate, sequenceNumber } = event.value;
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    const allowInfo = InformationBarUtils.allowInfo;
    const accessViewer = ((releaseDate) || (!allowInfo));
    const route = accessViewer ? ApplicationRoute.INTAKES_OUTPUTS_VIEWER : ApplicationRoute.INTAKES_OUTPUTS_EDITOR;

    IntakesAndPutsContext.setInternalNavegation(true);

    applicationContext.redirect(route, {
      encounterId,
      patientId,
      sequenceNumber
    });
  }

  @OnClick({ target: IntakesAndOutputsListAliases.INTAKES_OPTION })
  handleIntakeOptionClick(): void {
    applicationContext.redirect(ApplicationRoute.INTAKES_OUTPUTS_CREATE, { type: CreateType.INTAKE });
  }

  @OnClick({ target: IntakesAndOutputsListAliases.OUTPUTS_OPTION })
  handleOutputOptionClick(): void {
    applicationContext.redirect(ApplicationRoute.INTAKES_OUTPUTS_CREATE, { type: CreateType.OUTPUT });
  }

  @OnBeforeSubmit({ target: IntakesAndOutputsListAliases.FILTER_INTAKES_OUTPUTS })
  handleFilterIntakesOutputsBeforeSubmit(event: SubmitEvent) : void {
    const dateLimit = 90;
    if(DateFilterUtils.diffDate(event.record.finalDate, event.record.initialDate) > dateLimit) {
      ShowToast.open(MessageService.translate('text.message.more-ninety-days'), ToastLevel.CAUTION);
    }
  }

  private checkItemEditPermission(): Promise<void> {
    const itemType = ItemPepType.INTAKES_OUTPUTS;
    return HttpService.get(Endpoint.PEP_CAN_EDIT_ITEM, { itemType })
      .then(response => {
        const configuration = response.body as RulesItemConfiguration;
        const isAllowEdit = configuration?.allowEditing === YesNoType.YES;
        IntakesAndPutsContext.setAllowEdit(isAllowEdit);
      })
  }
}

export default IntakesAndOutputsController;
